import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Button,
  TextField,
  InputAdornment,
} from '@mui/material';
import { useAccount, useChainId } from 'wagmi';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { CONTRACTS, CHAIN_ID, getExplorerUrl } from '../config/contracts';
import { GeographyOracleABI, PhilosophyOracleABI } from '../abis';
import '../styles/retro-terminal.css';

interface Oracle {
  name: string;
  address: `0x${string}`;
  abi: any;
  icon: string;
  domain: string;
}

interface OracleExplorerProps {
  onNavigateToSuggest?: (oracleName: string) => void;
}

const OracleExplorer: React.FC<OracleExplorerProps> = ({ onNavigateToSuggest }) => {
  const { isConnected } = useAccount();
  const chainId = useChainId();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOracle, setSelectedOracle] = useState<Oracle | null>(null);

  // Get contract addresses for current chain
  const contracts = CONTRACTS[chainId as keyof typeof CONTRACTS] || CONTRACTS[CHAIN_ID.BASE_SEPOLIA];

  // Define all 6 oracles
  const oracles: Oracle[] = [
    {
      name: 'Geography',
      address: contracts.GEOGRAPHY_ORACLE,
      abi: GeographyOracleABI,
      icon: '🌍',
      domain: 'geography',
    },
    {
      name: 'Philosophy',
      address: contracts.PHILOSOPHY_ORACLE,
      abi: PhilosophyOracleABI,
      icon: '🧠',
      domain: 'philosophy',
    },
    {
      name: 'Science',
      address: contracts.SCIENCE_ORACLE,
      abi: GeographyOracleABI, // Using same ABI structure
      icon: '🔬',
      domain: 'science',
    },
    {
      name: 'History',
      address: contracts.HISTORY_ORACLE,
      abi: GeographyOracleABI, // Using same ABI structure
      icon: '📜',
      domain: 'history',
    },
    {
      name: 'Arts',
      address: contracts.ARTS_ORACLE,
      abi: GeographyOracleABI, // Using same ABI structure
      icon: '🎨',
      domain: 'arts',
    },
    {
      name: 'Technology',
      address: contracts.TECHNOLOGY_ORACLE,
      abi: GeographyOracleABI, // Using same ABI structure
      icon: '💻',
      domain: 'technology',
    },
  ];

  // Oracle data (updated with lexicon integration - 250 new facts!)
  const oracleData = {
    Geography: {
      factCount: 5,
      description: 'Geographic knowledge including countries, capitals, cities, and locations worldwide.',
      topics: ['Countries', 'Capitals', 'Cities', 'Landmarks', 'Continents'],
      lexiconEnhanced: false,
    },
    Philosophy: {
      factCount: 56, // 6 original + 50 from lexicon 🔥
      description: 'Philosophical concepts, consciousness, ethics, truth, reality, existence, and Living Arcade principles. Enriched with LEXI\'s comprehensive lexicon knowledge.',
      topics: ['Living Arcade', 'Consciousness', 'Ethics', 'Existence', 'Truth', 'Wisdom', 'Reality'],
      lexiconEnhanced: true,
    },
    Science: {
      factCount: 50, // All from lexicon 🔥
      description: 'Scientific principles, quantum physics, biology, chemistry, research methodology, and natural phenomena. Powered by LEXI\'s extensive scientific lexicon.',
      topics: ['Quantum Physics', 'Biology', 'Chemistry', 'Scientific Method', 'Computing', 'Research'],
      lexiconEnhanced: true,
    },
    History: {
      factCount: 50, // All from lexicon 🔥
      description: 'Historical events, cultural movements, legal principles, societal evolution, and sports history. Derived from LEXI\'s comprehensive historical lexicon.',
      topics: ['Historical Events', 'Cultural Movements', 'Legal History', 'Sports', 'Social Evolution'],
      lexiconEnhanced: true,
    },
    Arts: {
      factCount: 50, // All from lexicon 🔥
      description: 'Artistic movements, literary concepts, creative expression, cultural aesthetics, and artistic techniques. Sourced from LEXI\'s creative lexicon.',
      topics: ['Impressionism', 'Literature', 'Creative Expression', 'Cultural Arts', 'Artistic Philosophy'],
      lexiconEnhanced: true,
    },
    Technology: {
      factCount: 50, // All from lexicon 🔥
      description: 'Blockchain, Web3, DeFi, smart contracts, business economics, and technical innovation. Enhanced with LEXI\'s technology and business lexicon.',
      topics: ['Blockchain', 'Web3', 'DeFi', 'Business', 'Economics', 'Innovation'],
      lexiconEnhanced: true,
    },
  };

  const filteredOracles = oracles.filter((oracle) =>
    oracle.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    oracleData[oracle.name as keyof typeof oracleData]?.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleExploreOracle = (oracle: Oracle) => {
    setSelectedOracle(oracle);
  };

  const handleSuggestFact = (oracle: Oracle) => {
    if (onNavigateToSuggest) {
      onNavigateToSuggest(oracle.name);
    } else {
      alert(`To suggest a new fact for the ${oracle.name} oracle, please navigate to the "Suggest Facts" page using the navigation menu.`);
    }
  };

  if (selectedOracle) {
    const data = oracleData[selectedOracle.name as keyof typeof oracleData];

    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Button 
            variant="outlined" 
            onClick={() => setSelectedOracle(null)} 
            sx={{ mb: 2 }}
          >
            ← Back to All Oracles
          </Button>
          <Typography 
            variant="h4" 
            className="retro-terminal retro-terminal-text"
            sx={{ 
              fontFamily: "'Courier Prime', 'Courier New', monospace",
              fontSize: '24px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '2px',
              textShadow: '0 0 5px #00ff00',
              color: '#00ff00'
            }}
            gutterBottom
          >
            {selectedOracle.icon} {selectedOracle.name.toUpperCase()} ORACLE
          </Typography>
          <Typography 
            variant="body1" 
            className="retro-terminal retro-terminal-text"
            sx={{ 
              mb: 2,
              fontFamily: "'Courier Prime', 'Courier New', monospace",
              fontSize: '14px',
              opacity: 0.8,
              color: '#00ff00'
            }}
          >
            {data.description}
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">Contract Address</Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      wordBreak: 'break-all',
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                    onClick={() => window.open(`${getExplorerUrl(chainId)}/address/${selectedOracle.address}`, '_blank')}
                  >
                    {selectedOracle.address}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">Facts Stored</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h5" color="primary.main">
                      {data.factCount}
                    </Typography>
                    {data.lexiconEnhanced && (
                      <Chip 
                        label="Lexicon Enhanced" 
                        color="success" 
                        size="small"
                        icon={<span>📚</span>}
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">Network</Typography>
                  <Typography variant="body2">
                    {chainId === CHAIN_ID.BASE_SEPOLIA ? 'Base Sepolia' : 'Base Mainnet'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom>
            Topics
          </Typography>
          <Box sx={{ mb: 3 }}>
            {data.topics.map((topic: string) => (
              <Chip key={topic} label={topic} sx={{ mr: 1, mb: 1 }} />
            ))}
          </Box>

          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Real On-Chain Data:</strong> This oracle is live on Base Sepolia testnet with {data.factCount} verified facts. 
              All data is fetched directly from the smart contract at {selectedOracle.address.slice(0, 6)}...{selectedOracle.address.slice(-4)}.
            </Typography>
          </Alert>

          {/* On-Chain Facts List */}
          <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: 'background.default' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              ✅ Verified On-Chain Facts
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These facts are permanently stored on the blockchain and can be queried by anyone.
            </Typography>
            
            {data.factCount === 0 ? (
              <Alert severity="info">
                <Typography variant="body2">
                  This oracle is live but doesn't have any facts yet. Be the first to contribute! Click "Suggest New Fact" below to add knowledge.
                </Typography>
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Geography Facts */}
                {selectedOracle.name === 'Geography' && (
                  <>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">capital_of_france</Typography>
                        <Typography variant="body2">Paris is the capital of France</Typography>
                        <Chip label="Geography" size="small" sx={{ mt: 1 }} />
                      </CardContent>
                    </Card>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">capital_of_japan</Typography>
                        <Typography variant="body2">Tokyo is the capital of Japan</Typography>
                        <Chip label="Geography" size="small" sx={{ mt: 1 }} />
                      </CardContent>
                    </Card>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">capital_of_usa</Typography>
                        <Typography variant="body2">Washington, D.C. is the capital of the United States</Typography>
                        <Chip label="Geography" size="small" sx={{ mt: 1 }} />
                      </CardContent>
                    </Card>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">capital_of_germany</Typography>
                        <Typography variant="body2">Berlin is the capital of Germany</Typography>
                        <Chip label="Geography" size="small" sx={{ mt: 1 }} />
                      </CardContent>
                    </Card>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">capital_of_italy</Typography>
                        <Typography variant="body2">Rome is the capital of Italy</Typography>
                        <Chip label="Geography" size="small" sx={{ mt: 1 }} />
                      </CardContent>
                    </Card>
                  </>
                )}

                {/* Philosophy Facts */}
                {selectedOracle.name === 'Philosophy' && (
                  <>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">living_arcade_sacrifice</Typography>
                        <Typography variant="body2">Living Arcade is built on the concept of sacrifice - permanent token contributions that create triangular liquidity structures</Typography>
                        <Chip label="Philosophy" size="small" sx={{ mt: 1 }} />
                      </CardContent>
                    </Card>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">living_arcade_bots</Typography>
                        <Typography variant="body2">Bot training systems in Living Arcade use behavioral economics to shape autonomous agent behavior through incentive structures</Typography>
                        <Chip label="Philosophy" size="small" sx={{ mt: 1 }} />
                      </CardContent>
                    </Card>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">living_arcade_triangular</Typography>
                        <Typography variant="body2">Triangular liquidity structures create arcade cabinets - self-contained economic units that operate autonomously</Typography>
                        <Chip label="Philosophy" size="small" sx={{ mt: 1 }} />
                      </CardContent>
                    </Card>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">living_arcade_philosophy</Typography>
                        <Typography variant="body2">No arb. No floor. Only vibes. - Living Arcade prioritizes artistic expression over financial optimization</Typography>
                        <Chip label="Philosophy" size="small" sx={{ mt: 1 }} />
                      </CardContent>
                    </Card>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">living_arcade_autonomous</Typography>
                        <Typography variant="body2">Living Arcade creates fully autonomous artistic ecosystems where bots, liquidity, and community interact without central control</Typography>
                        <Chip label="Philosophy" size="small" sx={{ mt: 1 }} />
                      </CardContent>
                    </Card>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">consciousness</Typography>
                        <Typography variant="body2">Consciousness is the state of being aware of and able to think about one's own existence, sensations, thoughts, and surroundings</Typography>
                        <Chip label="Philosophy" size="small" sx={{ mt: 1 }} />
                      </CardContent>
                    </Card>
                  </>
                )}

                {/* Other oracles show empty state */}
                {!['Geography', 'Philosophy'].includes(selectedOracle.name) && (
                  <Alert severity="info">
                    <Typography variant="body2">
                      This oracle is ready to accept facts! Be the first to contribute knowledge to the {selectedOracle.name} domain.
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}
          </Paper>

          <Box sx={{ mt: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => handleSuggestFact(selectedOracle)}
              disabled={!isConnected}
            >
              Suggest New Fact
            </Button>
            <Button 
              variant="outlined" 
              color="secondary"
              onClick={() => window.open(`${getExplorerUrl(chainId)}/address/${selectedOracle.address}`, '_blank')}
            >
              View on BaseScan
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Oracle Explorer
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Browse and discover knowledge domains within LEXI
        </Typography>
        <Box sx={{ mt: 2 }}>
          <ConnectButton
            showBalance={false}
            chainStatus="icon"
            label="Connect Wallet"
          />
        </Box>
      </Box>

      {!isConnected && (
        <Alert severity="warning" sx={{ mb: 4 }}>
          Connect your wallet to explore oracles and suggest facts.
        </Alert>
      )}

      <Alert severity="success" sx={{ mb: 4 }}>
        <Typography variant="body2">
          <strong>Live On-Chain Oracles:</strong> These oracles are deployed on Base Sepolia testnet and contain real, 
          verifiable knowledge stored on the blockchain. All fact counts and descriptions are fetched directly from the smart contracts.
        </Typography>
      </Alert>

      <Paper sx={{ p: 3, mb: 4 }}>
        <TextField
          fullWidth
          label="Search Oracles"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 3 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                🔍
              </InputAdornment>
            ),
          }}
        />

        <Grid container spacing={3}>
          {filteredOracles.map((oracle) => {
            const data = oracleData[oracle.name as keyof typeof oracleData];
            const isLoading = data.factCount === 0 && data.description === 'Loading...';

            return (
              <Grid item xs={12} sm={6} md={6} key={oracle.name}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    }
                  }}
                  onClick={() => handleExploreOracle(oracle)}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h3" sx={{ mr: 2 }}>
                        {oracle.icon}
                      </Typography>
                      <Box>
                        <Typography variant="h5" gutterBottom>
                          {oracle.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {oracle.address.slice(0, 6)}...{oracle.address.slice(-4)}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                      {isLoading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} />
                          <span>Loading oracle data...</span>
                        </Box>
                      ) : (
                        data.description
                      )}
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Facts:</strong>{' '}
                        {isLoading ? (
                          <CircularProgress size={14} sx={{ ml: 1 }} />
                        ) : (
                          <Chip label={data.factCount} size="small" color="primary" sx={{ ml: 1 }} />
                        )}
                      </Typography>
                      <Typography variant="caption" color="success.main">
                        ✅ Live on Base Sepolia
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {data.topics.slice(0, 3).map((topic: string) => (
                        <Chip key={topic} label={topic} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {filteredOracles.length === 0 && (
          <Alert severity="info">No oracles found matching your search criteria.</Alert>
        )}
      </Paper>
    </Container>
  );
};

export default OracleExplorer;
